import { useCallback, useRef, useState } from "react";
import Webcam from "react-webcam";
import CountdownButton from "./CountdownButton";
import { useTranslation } from "@/hooks/useTranslation";
import { Button } from "../ui/button";
import { VideoIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "../ui/alert";
import { isIOS } from "react-device-detect";
import { Capacitor } from "@capacitor/core";

interface StatusMessage {
  type: "success" | "error" | "info";
  message: string;
}

export default function VideoRecorder({
  videoSeconds,
  onComplete,
}: {
  videoSeconds: number;
  onComplete: (blob: Blob) => void;
}) {
  const t = useTranslation();
  const { toast } = useToast();
  const webcamRef = useRef<Webcam>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const [capturing, setCapturing] = useState<boolean>(false);
  const [status, setStatus] = useState<StatusMessage | null>(null);

  // iOS WebKit (26.5+) reports webm as supported via isTypeSupported but produces an
  // empty/unencodable file when actually recording it, which the backend rejects as
  // "invalid file". iOS only truly encodes mp4, and the backend transcodes everything
  // to H.264/mp4 anyway, so always record mp4 on iOS (Capacitor app and Safari alike).
  // isIOS (UA-based) misses iPadOS-as-desktop, so also check the Capacitor platform.
  const isIOSDevice = isIOS || Capacitor.getPlatform() === "ios";
  const isWebm =
    !isIOSDevice &&
    typeof MediaRecorder !== "undefined" &&
    MediaRecorder.isTypeSupported("video/webm");
  const mimeType = isWebm ? "video/webm" : "video/mp4";

  const handleDataAvailable = useCallback(
    ({ data }: BlobEvent) => {
      if (data.size > 0) {
        recordedChunksRef.current.push(data);
      }

      if (
        mediaRecorderRef.current?.state === "inactive" &&
        recordedChunksRef.current.length
      ) {
        const finalBlob = new Blob(recordedChunksRef.current, {
          type: mimeType,
        });
        onComplete(finalBlob);
        recordedChunksRef.current = [];
      }
    },
    [mimeType, onComplete]
  );

  const handleStartCaptureClick = useCallback(() => {
    setCapturing(true);
    recordedChunksRef.current = [];

    if (webcamRef.current && webcamRef.current.stream) {
      mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
        mimeType,
      });

      mediaRecorderRef.current.addEventListener(
        "dataavailable",
        handleDataAvailable
      );
      mediaRecorderRef.current.start();
    }
  }, [handleDataAvailable, mimeType]);

  const handleStopCaptureClick = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setCapturing(false);
    }
  }, []);

  return (
    <>
      <div className="relative w-full pt-[100%] overflow-hidden rounded-lg mb-4">
        <Webcam
          audio={false}
          ref={webcamRef}
          videoConstraints={{
            facingMode: "user",
            width: 640,
            height: 480,
          }}
          mirrored={true}
          className="absolute top-0 left-0 w-full h-full object-cover"
          onUserMediaError={(error: string | DOMException) => {
            const errorMessage =
              error instanceof DOMException ? error.message : error;
            setStatus({
              type: "error",
              message: t.FACE_VERIFICATION.ERRORS.CAMERA(errorMessage),
            });
            toast({
              title: "Camera error",
              description: errorMessage,
              variant: "destructive",
              className: "dark",
            });
          }}
        />
      </div>
      <div className="flex justify-center mb-4">
        {capturing ? (
          <CountdownButton
            initialSeconds={videoSeconds}
            onStop={handleStopCaptureClick}
            t={t}
          />
        ) : (
          <Button
            onClick={handleStartCaptureClick}
            aria-label="Start recording for 6 seconds"
            className="w-full"
          >
            <VideoIcon className="mr-2 h-4 w-4" />
            {t.FACE_VERIFICATION.LABELS.RECORD_BUTTON}
          </Button>
        )}
      </div>
      {status && (
        <Alert variant={status.type === "error" ? "destructive" : "default"}>
          <AlertDescription>{status.message}</AlertDescription>
        </Alert>
      )}
    </>
  );
}
